import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useClient } from '../context/ClientContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PlusCircle, Trash2, ChefHat, Star, Zap, HelpCircle, AlertCircle, Pencil, Sparkles, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { AIInsightsPanel } from '../components/AIInsightsPanel';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { PendingImports } from '../components/PendingImports';

const CLASSIFICATION_CONFIG = {
  Star: { color: 'text-state-success', bg: 'bg-green-50', icon: Star, desc: 'High margin & high popularity' },
  Plowhorse: { color: 'text-state-warning', bg: 'bg-yellow-50', icon: Zap, desc: 'Popular but low margin' },
  Puzzle: { color: 'text-state-info', bg: 'bg-blue-50', icon: HelpCircle, desc: 'High margin but unpopular' },
  Dog: { color: 'text-state-error', bg: 'bg-red-50', icon: AlertCircle, desc: 'Low margin & unpopular' },
  Unsold: { color: 'text-text-muted', bg: 'bg-gray-50', icon: AlertCircle, desc: 'No sales recorded' }
};

export const MenuEngineering = () => {
  const { selectedClient, API } = useClient();
  const location = useLocation();
  // When accessed as `/menu` this is the plain Menu tab (items + prices, no
  // profitability KPIs/analytics).  When accessed as `/menu-engineering` it
  // renders the full analysis surface (operator only, per nav config).
  const isSimpleMenu = location.pathname === '/menu';
  const [profitability, setProfitability] = useState(null);
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('All');

  const fetchData = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const [profRes, itemsRes] = await Promise.all([
        axios.get(`${API}/menu/profitability/${selectedClient.id}?year=${year}&month=${month}`),
        axios.get(`${API}/menu/items/${selectedClient.id}`)
      ]);
      setProfitability(profRes.data);
      setItems(itemsRes.data.items);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedClient, year, month]);

  if (!selectedClient) {
    return <div className="text-center text-text-secondary py-12">Select a client to view menu engineering</div>;
  }

  const filteredRows = profitability?.rows.filter(r => 
    categoryFilter === 'All' || r.category === categoryFilter
  ) || [];

  return (
    <div className="space-y-6" data-testid="menu-engineering-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Menu Engineering</h2>
          <p className="text-text-secondary mt-1">Item-level profitability analysis - {selectedClient.name}</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="border border-border rounded-md px-3 py-2 text-sm bg-white"
            data-testid="month-selector"
          >
            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{new Date(2026, m-1, 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border border-border rounded-md px-3 py-2 text-sm bg-white"
            data-testid="year-selector"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <AddMenuItemDialog clientId={selectedClient.id} onAdded={fetchData} />
        </div>
      </div>

      <Tabs defaultValue={isSimpleMenu ? "items" : "pivot"} className="space-y-6">
        <TabsList className="bg-white border border-border">
          {!isSimpleMenu && <TabsTrigger value="pivot" data-testid="tab-pivot">Pivot Analysis</TabsTrigger>}
          {!isSimpleMenu && <TabsTrigger value="ai-insights" data-testid="tab-menu-ai-insights">
            <Sparkles className="h-3 w-3 mr-1 text-accent" /> AI Insights
          </TabsTrigger>}
          <TabsTrigger value="items" data-testid="tab-items">Manage Items & Prices</TabsTrigger>
          {!isSimpleMenu && <TabsTrigger value="sales" data-testid="tab-sales">Record Sales</TabsTrigger>}
          {!isSimpleMenu && <TabsTrigger value="upload" data-testid="tab-menu-upload">
            <Sparkles className="h-3 w-3 mr-1 text-state-info" /> AI Upload Menu
          </TabsTrigger>}
        </TabsList>

        <TabsContent value="pivot" className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-text-muted">Loading...</div>
          ) : profitability && (
            <>
              {/* Summary Cards — Menu Engineering only; hidden on /menu */}
              {!isSimpleMenu && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="menu-summary-kpis">
                  <SummaryCard label="Total Revenue" value={`₹${profitability.totals.total_revenue.toLocaleString()}`} />
                  <SummaryCard label="Total Cost" value={`₹${profitability.totals.total_cost.toLocaleString()}`} />
                  <SummaryCard label="Profit" value={`₹${profitability.totals.total_profit.toLocaleString()}`} />
                  <SummaryCard label="Avg Margin" value={`${profitability.totals.average_margin}%`} />
                </div>
              )}

              {/* Classification Legend — analytics only */}
              {!isSimpleMenu && (
                <div className="bg-white border border-border rounded-md p-4" data-testid="menu-engineering-legend">
                <p className="text-xs uppercase tracking-[0.2em] text-text-secondary mb-3 font-semibold">Menu Engineering Classifications</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(CLASSIFICATION_CONFIG).slice(0, 4).map(([name, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <div key={name} className={`flex items-center space-x-2 ${cfg.bg} rounded-md px-3 py-2`}>
                        <Icon className={`h-4 w-4 ${cfg.color}`} />
                        <div>
                          <p className={`text-sm font-semibold ${cfg.color}`}>{name}</p>
                          <p className="text-xs text-text-muted">{cfg.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              )}

              {/* Filter */}
              <div className="flex space-x-2">
                {['All', 'Food', 'Beverage'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      categoryFilter === cat
                        ? 'bg-primary text-white'
                        : 'bg-white border border-border text-text-secondary hover:border-primary'
                    }`}
                    data-testid={`filter-${cat.toLowerCase()}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Pivot Table */}
              <div className="bg-white border border-border rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Cost</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Sell</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Qty</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Total Cost</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Profit</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Margin %</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Class</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredRows.map((row, idx) => {
                        const cfg = CLASSIFICATION_CONFIG[row.classification];
                        const Icon = cfg.icon;
                        return (
                          <tr key={row.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'} data-testid={`menu-row-${row.id}`}>
                            <td className="px-4 py-3 text-sm font-medium text-text-primary">{row.name}</td>
                            <td className="px-4 py-3 text-sm text-text-secondary">
                              <span className="text-xs">{row.category}</span>
                              {row.sub_category && <span className="text-xs text-text-muted block">{row.sub_category}</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">₹{row.cost_price}</td>
                            <td className="px-4 py-3 text-sm text-right">₹{row.selling_price}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium">{row.quantity_sold}</td>
                            <td className="px-4 py-3 text-sm text-right">₹{row.total_revenue.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-right text-text-muted">₹{row.total_cost.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-state-success">₹{row.profit.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">{row.margin_percentage}%</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center space-x-1 ${cfg.bg} ${cfg.color} px-2 py-1 rounded text-xs font-semibold`}>
                                <Icon className="h-3 w-3" />
                                <span>{row.classification}</span>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-primary/5 font-semibold">
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-sm text-primary">TOTALS</td>
                        <td className="px-4 py-3 text-sm text-right text-primary">{profitability.totals.total_quantity}</td>
                        <td className="px-4 py-3 text-sm text-right text-primary">₹{profitability.totals.total_revenue.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-right text-primary">₹{profitability.totals.total_cost.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-right text-primary">₹{profitability.totals.total_profit.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-right text-primary">{profitability.totals.average_margin}%</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="ai-insights">
          {selectedClient && (
            <AIInsightsPanel
              endpoint={`${API}/ai/insights/menu/${selectedClient.id}`}
              params={{ year, month }}
              title="Menu Engineering AI Insights"
              testId="menu-ai-insights"
            />
          )}
        </TabsContent>

        <TabsContent value="items">
          <ItemsManager items={items} clientId={selectedClient.id} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="sales">
          <RecordSalesPanel items={items} clientId={selectedClient.id} onSuccess={fetchData} />
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <ModuleUploadZone module="menu" moduleLabel="Menu / Price List"
            clientId={selectedClient.id} API={API} onUploadComplete={fetchData} />
          <PendingImports clientId={selectedClient.id} API={API} module="menu" onChange={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="bg-white border border-border rounded-md p-4">
    <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">{label}</p>
    <p className="text-2xl font-bold text-primary">{value}</p>
  </div>
);

const AddMenuItemDialog = ({ clientId, onAdded }) => {
  const { API } = useClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', category: 'Food', sub_category: '', cost_price: '', selling_price: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/menu/items`, {
        client_id: clientId,
        name: form.name,
        category: form.category,
        sub_category: form.sub_category || null,
        cost_price: parseFloat(form.cost_price),
        selling_price: parseFloat(form.selling_price)
      });
      setOpen(false);
      setForm({ name: '', category: 'Food', sub_category: '', cost_price: '', selling_price: '' });
      onAdded();
    } catch (err) {
      alert('Error adding item');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary-hover" data-testid="add-menu-item-button">
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Item
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Menu Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Item Name"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full border border-border rounded-md px-3 py-2"
            required
            data-testid="item-name-input"
          />
          <select
            value={form.category}
            onChange={(e) => setForm({...form, category: e.target.value})}
            className="w-full border border-border rounded-md px-3 py-2"
            data-testid="item-category-select"
          >
            <option value="Food">Food</option>
            <option value="Beverage">Beverage</option>
          </select>
          <input
            type="text"
            placeholder="Sub-category (e.g., Appetizer, Main Course)"
            value={form.sub_category}
            onChange={(e) => setForm({...form, sub_category: e.target.value})}
            className="w-full border border-border rounded-md px-3 py-2"
            data-testid="item-sub-category-input"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              step="0.01"
              placeholder="Cost Price (₹)"
              value={form.cost_price}
              onChange={(e) => setForm({...form, cost_price: e.target.value})}
              className="w-full border border-border rounded-md px-3 py-2"
              required
              data-testid="item-cost-input"
            />
            <input
              type="number"
              step="0.01"
              placeholder="Selling Price (₹)"
              value={form.selling_price}
              onChange={(e) => setForm({...form, selling_price: e.target.value})}
              className="w-full border border-border rounded-md px-3 py-2"
              required
              data-testid="item-price-input"
            />
          </div>
          <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" data-testid="submit-item-button">
            Add Item
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ItemsManager = ({ items, clientId, onRefresh }) => {
  const { API } = useClient();
  const [editingItem, setEditingItem] = useState(null);

  const handleDelete = async (itemId) => {
    if (!confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/menu/items/${itemId}`);
      onRefresh();
    } catch (err) {
      alert('Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Data Source Transparency Notice */}
      <div className="bg-blue-50 border-l-4 border-state-info rounded-md p-4" data-testid="data-source-notice">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-state-info flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-state-info mb-1">Where do these prices come from?</p>
            <ul className="text-text-secondary space-y-1 text-xs">
              <li>• <strong>Cost Price</strong>: Manually entered by you when adding an item, or imported via CSV. Click pencil icon to update.</li>
              <li>• <strong>Selling Price</strong>: Your menu price - what you charge customers. Edit to reflect menu changes.</li>
              <li>• <strong>Market Reference</strong>: Optional - your benchmark for "what this should cost". Click ✨ to ask AI to estimate market rate.</li>
              <li>• All price changes are tracked in history with timestamp.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-primary">All Menu Items ({items.length})</h3>
          <p className="text-xs text-text-muted">Click <Pencil className="inline h-3 w-3" /> to edit prices</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(item => (
            <div key={item.id} className="border border-border rounded-md p-4 hover:border-primary/30 transition-colors" data-testid={`item-${item.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="font-medium text-text-primary">{item.name}</p>
                  <p className="text-xs text-text-muted">{item.category} • {item.sub_category}</p>
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => setEditingItem(item)}
                    className="text-primary hover:bg-background-secondary rounded p-1"
                    data-testid={`edit-item-${item.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => handleDelete(item.id)} 
                    className="text-state-error hover:bg-red-50 rounded p-1"
                    data-testid={`delete-item-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <PriceTag label="Cost" value={item.cost_price} />
                <PriceTag label="Sell" value={item.selling_price} color="text-state-success" />
                <PriceTag 
                  label="Market" 
                  value={item.market_reference_price} 
                  color="text-state-info"
                  empty="—"
                />
              </div>
              {item.market_reference_price && item.cost_price > item.market_reference_price && (
                <p className="text-xs text-state-warning mt-2 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Your cost is ₹{(item.cost_price - item.market_reference_price).toFixed(0)} above market reference
                </p>
              )}
              {item.last_price_update && (
                <p className="text-xs text-text-muted mt-2">
                  Last updated: {new Date(item.last_price_update).toLocaleDateString()}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {editingItem && (
        <EditMenuItemDialog 
          item={editingItem} 
          onClose={() => setEditingItem(null)} 
          onSaved={() => { onRefresh(); setEditingItem(null); }}
        />
      )}
    </div>
  );
};

const PriceTag = ({ label, value, color = 'text-text-primary', empty = '0' }) => (
  <div className="bg-background-secondary rounded p-2">
    <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</p>
    <p className={`text-sm font-bold ${color}`}>{value !== null && value !== undefined ? `₹${value}` : empty}</p>
  </div>
);

const EditMenuItemDialog = ({ item, onClose, onSaved }) => {
  const { API } = useClient();
  const [form, setForm] = useState({
    cost_price: item.cost_price,
    selling_price: item.selling_price,
    market_reference_price: item.market_reference_price || ''
  });
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios.get(`${API}/price-history/${item.client_id}?entity_type=menu_item&entity_id=${item.id}`)
      .then(res => setHistory(res.data.history || []))
      .catch(() => {});
  }, [item.id]);

  const estimateMarketPrice = async () => {
    setEstimating(true);
    try {
      const res = await axios.post(`${API}/ai/estimate-market-price?item_name=${encodeURIComponent(item.name)}&category=${encodeURIComponent(item.category)}`);
      if (res.data.estimated_price) {
        setForm(prev => ({...prev, market_reference_price: res.data.estimated_price}));
        alert(`AI estimated: ₹${res.data.estimated_price}/${res.data.unit}\nConfidence: ${res.data.confidence}\nNote: ${res.data.notes}`);
      } else {
        alert('Could not estimate. Please enter manually.');
      }
    } catch (e) {
      alert('Estimation failed');
    } finally {
      setEstimating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/menu/items/${item.id}`, {
        cost_price: parseFloat(form.cost_price),
        selling_price: parseFloat(form.selling_price),
        market_reference_price: form.market_reference_price ? parseFloat(form.market_reference_price) : null
      });
      onSaved();
    } catch (err) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Prices: {item.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">Cost Price (₹)</label>
              <input type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm({...form, cost_price: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="edit-cost-price" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">Selling Price (₹)</label>
              <input type="number" step="0.01" value={form.selling_price} onChange={(e) => setForm({...form, selling_price: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="edit-selling-price" />
            </div>
          </div>
          <div>
            <label className="flex items-center justify-between text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
              <span>Market Reference Price (₹)</span>
              <button type="button" onClick={estimateMarketPrice} disabled={estimating} className="text-accent hover:text-accent-hover text-xs flex items-center space-x-1 normal-case" data-testid="estimate-market-button">
                <Sparkles className="h-3 w-3" />
                <span>{estimating ? 'Estimating...' : 'Ask AI'}</span>
              </button>
            </label>
            <input type="number" step="0.01" value={form.market_reference_price} onChange={(e) => setForm({...form, market_reference_price: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" placeholder="Optional - typical market rate" data-testid="edit-market-price" />
            <p className="text-xs text-text-muted mt-1">Optional benchmark to compare against your supplier cost</p>
          </div>

          {history.length > 0 && (
            <div className="border-t border-border pt-3">
              <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">Price History</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {history.map((h, idx) => (
                  <div key={idx} className="text-xs flex justify-between text-text-secondary bg-background-secondary rounded px-2 py-1">
                    <span>{h.field}: ₹{h.old_value} → ₹{h.new_value}</span>
                    <span className="text-text-muted">{new Date(h.changed_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary-hover" data-testid="save-edit-button">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RecordSalesPanel = ({ items, clientId, onSuccess }) => {
  const { API } = useClient();
  const [form, setForm] = useState({
    menu_item_id: '',
    date: new Date().toISOString().slice(0, 10),
    quantity_sold: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/menu/sales`, {
        client_id: clientId,
        menu_item_id: form.menu_item_id,
        date: form.date,
        quantity_sold: parseInt(form.quantity_sold)
      });
      setForm({...form, quantity_sold: ''});
      onSuccess();
      alert('Sale recorded successfully');
    } catch (err) {
      alert('Error recording sale');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-md p-8 max-w-2xl">
      <h3 className="text-lg font-semibold text-primary mb-4">Record Item Sale</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Menu Item</label>
          <select
            value={form.menu_item_id}
            onChange={(e) => setForm({...form, menu_item_id: e.target.value})}
            className="w-full border border-border rounded-md px-3 py-2"
            required
            data-testid="sale-item-select"
          >
            <option value="">Select item</option>
            {items.map(item => (
              <option key={item.id} value={item.id}>{item.name} (₹{item.selling_price})</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-2">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({...form, date: e.target.value})}
              className="w-full border border-border rounded-md px-3 py-2"
              required
              data-testid="sale-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Quantity Sold</label>
            <input
              type="number"
              value={form.quantity_sold}
              onChange={(e) => setForm({...form, quantity_sold: e.target.value})}
              className="w-full border border-border rounded-md px-3 py-2"
              required
              data-testid="sale-quantity-input"
            />
          </div>
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-primary hover:bg-primary-hover" data-testid="submit-sale-button">
          {submitting ? 'Recording...' : 'Record Sale'}
        </Button>
      </form>
    </div>
  );
};
